import { FC, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Plus, X } from 'lucide-react';
import { trpc } from 'src/trpc';
import { useOrganization } from 'src/context/OrganizationContext';
import { useToast } from 'components/useToast';
import { Label } from '@/components/ui/label';

export const NewCleanupModal: FC = () => {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [descriptionItems, setDescriptionItems] = useState<string[]>(['']);
  const [isTemplate, setIsTemplate] = useState(false);
  const [position, setPosition] = useState<number | undefined>(undefined);
  const { successToast, errorToast } = useToast();

  const { organizationId } = useOrganization();

  const utils = trpc.useUtils();

  const createCleanupMutation = trpc.admin.cleanup.createCleanup.useMutation({
    onSuccess: () => {
      successToast({ title: 'Cleanup created successfully' });
      setOpen(false);
      resetForm();
      utils.admin.cleanup.getCleanups.invalidate();
    },
    onError: (error) => {
      errorToast({ title: 'Failed to create cleanup', description: error.message });
    },
  });

  const resetForm = () => {
    setTitle('');
    setDescriptionItems(['']);
    setIsTemplate(false);
    setPosition(undefined);
  };

  const addDescriptionItem = () => {
    setDescriptionItems([...descriptionItems, '']);
  };

  const removeDescriptionItem = (index: number) => {
    if (descriptionItems.length > 1) {
      setDescriptionItems(descriptionItems.filter((_, i) => i !== index));
    }
  };

  const updateDescriptionItem = (index: number, value: string) => {
    const newItems = [...descriptionItems];
    newItems[index] = value;
    setDescriptionItems(newItems);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      errorToast({ title: 'Title is required', description: 'Please provide a title for the cleanup.' });
      return;
    }

    if (descriptionItems.filter((item) => item.trim()).length === 0) {
      errorToast({ title: 'At least one description item is required', description: 'Please add at least one description item.' });
      return;
    }

    const cleanupData = {
      title: title.trim(),
      descriptionItems: descriptionItems.filter((item) => item.trim()),
      isTemplate,
      position,
      organizationId: organizationId || undefined,
    };

    try {
      await createCleanupMutation.mutateAsync(cleanupData);
    } catch (error) {
      // Error is handled by the mutation
    }
  };

  const isValid = title.trim() && descriptionItems.filter((item) => item.trim()).length > 0;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          New Cleanup
        </Button>
      </SheetTrigger>
      <SheetContent className="p-0 sm:max-w-[600px] lg:max-w-[700px] xl:max-w-[800px] h-full">
        <div className="grid h-full grid-rows-[auto_1fr_auto]">
          <SheetHeader className="border-b bg-white px-6 py-4">
            <SheetTitle className="text-left text-lg font-semibold text-gray-900">
              Create New Cleanup Service
            </SheetTitle>
          </SheetHeader>
          <div className="overflow-y-auto px-6 py-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Cleanup Service Title</Label>
                  <Input
                    id="title"
                    placeholder="Cleanup Service Title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </div>

                <div>
                  <div className="space-y-2">
                    <div className="space-y-2">
                      {descriptionItems.map((item, index: number) => (
                        <div key={index} className="flex gap-2 items-center">
                          <Label htmlFor="title" style={{ minWidth: 60 }}>
                            Item {index + 1}
                          </Label>
                          <Input
                            value={item}
                            placeholder={`Item ${index + 1} Description`}
                            onChange={(e) => updateDescriptionItem(index, e.target.value)}
                            required
                          />
                          {descriptionItems.length > 1 && (
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              onClick={() => removeDescriptionItem(index)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>

                    <Button
                      type="button"
                      variant="outline"
                      onClick={addDescriptionItem}
                      className="w-full"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Description Item
                    </Button>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-1">
                    <Label htmlFor="position">Position</Label>
                    <Input
                      type="number"
                      value={position || ''}
                      placeholder="Position"
                      onChange={(e) => setPosition(Number(e.target.value))}
                      min={1}
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      id="isTemplate"
                      type="checkbox"
                      checked={isTemplate}
                      onChange={(e) => setIsTemplate(e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    <label htmlFor="isTemplate" className="text-sm font-medium text-gray-700">
                      Template
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={!isValid || createCleanupMutation.isPending}>
                  {createCleanupMutation.isPending ? 'Creating...' : 'Create Cleanup'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
